<template>
  <div class="graph flex-grow-1 d-flex flex-col justify-content-center">
    <div
      class="d-flex flex-col justify-content-center"
      ref="charRef"
      style="height: 100%; width: 100%"
    ></div>
  </div>
</template>

<style scoped>
.graph {
  min-height: 200px;
  min-width: 200px;
  align-items: top;
}
</style>

<script>
import {
  onMounted,
  onUnmounted,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  computed,
  watch,
} from "vue";
import axios from "axios";
import * as echarts from "echarts";
import { useStore } from "vuex";
import { getGaugeOptions } from "@/data/gauge.data";
import { createQuery } from "@/data/createCPULoadQuery";

export default {
  name: "GraphCpuLoad",
  props: {
    host: { type: String, default: null },
    type: { type: String, default: null },
    term: { type: String, default: "shortterm" },
    refresh: { type: Number, default: 15 },
    title: { type: String, default: "1 Min" },
    cpuCount: { type: Number, default: 4 },
  },
  setup(props) {
    const timer = ref(null);
    const chart = ref(null);
    const charRef = ref(null);
    const graphRef = ref(null);
    const txtTitle = ref(null);
    const graphValue = ref(0);

    const store = useStore();
    const layoutMode = computed(() => store.state.layout.layoutMode);
    const apiKey = computed(() => store.state.auth.user?.accessToken);
    const apiHost = computed(() => window.apiHost);

    const resizeObserver = new ResizeObserver(() => onWindowResize());

    // =======================
    // Recreate chart on theme change
    // =======================
    watch(layoutMode, (newMode) => {
      if (chart.value !== null) {
        const theme = newMode === "dark" ? "dark" : "light";
        chart.value.dispose();
        chart.value = echarts.init(charRef.value, theme);
        drawChart(graphValue.value);
      }
    });

    // =======================
    // Initialize chart
    // =======================
    function initChart() {
      if (chart.value === null) {
        const theme = layoutMode.value === "dark" ? "dark" : "light";
        chart.value = echarts.init(charRef.value, theme);
      }

      const option = getGaugeOptions(props.cpuCount);
      option.backgroundColor =
        layoutMode.value === "dark" ? "#22354e" : "#ffffff";

      // ✅ Round displayed value to 4 digits
      option.series[0].detail.formatter = (val) => Number(val).toFixed(4);

      chart.value.setOption(option);
    }

    // =======================
    // Draw chart with value
    // =======================
    function drawChart(value) {
      graphValue.value = value;

      const option = getGaugeOptions(props.cpuCount);
      option.series[0].data[0].value = Number(Number(value || 0).toFixed(3)); // ✅ Rounded value
      option.series[0].data[0].name = txtTitle.value;
      option.series[0].title.fontSize = 22;

      option.series[0].detail.formatter = (val) => Number(val).toFixed(2); // ✅ round here too
      option.backgroundColor =
        layoutMode.value === "dark" ? "#22354e" : "#ffffff";

      chart.value.setOption(option);
    }

    // =======================
    // Fetch CPU data (Flux query)
    // =======================
    async function main() {
      try {
        const query = createQuery(
          props.host,
          props.term,
          props.type,
          props.cpuCount
        );

        const response = await axios.post(
          apiHost.value + "/core/api/v1/analytics/query",
          query,
          {
            headers: {
              Authorization: "Bearer " + apiKey.value,
              "Content-Type": "application/vnd.flux",
              Accept: "application/csv",
            },
          }
        );

        let data = response.data;
        let lines = -1;
        let value = 0;

        data.split("\n").forEach((line) => {
          if (++lines === 1) {
            const fields = line.trim().split(",");
            if (fields.length > 6) {
              value = parseFloat(fields[6]);
            }
          }
        });

        drawChart(Number(value) || 0); // ✅ Pass rounded number to chart
      } catch (error) {
        console.error("CPU Load Fetch Error:", error);
      }
    }

    // =======================
    // Handle resize
    // =======================
    function onWindowResize() {
      if (chart.value !== null) chart.value.resize();
    }

    // =======================
    // Lifecycle hooks
    // =======================
    onMounted(() => {
      initChart();

      txtTitle.value =
        props.term === "midterm"
          ? "5 Min"
          : props.term === "longterm"
          ? "15 Min"
          : props.title;

      main();

      if (props.refresh > 0) {
        timer.value = setInterval(main, props.refresh * 1000);
      }

      if (graphRef.value) resizeObserver.observe(graphRef.value);
    });

    onUnmounted(() => {
      if (props.refresh > 0 && timer.value) clearInterval(timer.value);
    });

    onBeforeMount(() => {
      window.addEventListener("resize", onWindowResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", onWindowResize);
      if (graphRef.value instanceof Element)
        resizeObserver.unobserve(graphRef.value);
    });

    return {
      charRef,
      graphRef,
      txtTitle,
    };
  },
};
</script>
